import React, { useContext, useState, useRef, FC, useEffect } from "react";
import * as Yup from "yup";
import {
  Formik,
  Form,
  ErrorMessage,
  FieldArray,
  useFormikContext,
  FormikHelpers,
  Field 
} from "formik";
import { onlineManager, useQuery } from "react-query";
import { isUndefined, sum, uniq } from "lodash";
import NumberFormat, { InputAttributes } from "react-number-format";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  loan,
  PaymentScheduleList,
  ProductsResponse,
} from "../@types/responses";
import { Track, Values } from "../@types/common";
import { BREAKPOINTS } from "../constants/common";
import { translation as t } from "../utils/translation";
import { APIContext } from "../context/context";
import { formatNumberWithoutDot } from "../utils/ui";
import { ReactComponent as Arrow } from "../assets/icons/arrow.svg";
import { ReactComponent as Pencil } from "../assets/icons/pencil.svg";
import { ReactComponent as CircleTrash } from "../assets/icons/circle-trash.svg";
import { ReactComponent as ILS } from "../assets/icons/ILS.svg";
import { useWindowSize } from "../hooks/useWindowWidth";
import { getPayloadProducts, rangeNumber } from "../utils/logic";
import CalculateByProductResults from "./calculateByProductResults";
import InterstRate from "./interestRate";
import Period from "./period";
import ErrorModal from "./errorModal";
import Loader from "./loader";
interface CustomProps {
  onChange: (values: any) => void;
}
const NumberFormatCustom = React.forwardRef<
  NumberFormat<InputAttributes>,
  CustomProps
>
(function NumberFormatCustom(props, ref) {  
  const { onChange, ...other } = props;
  return (
    <>
      <NumberFormat
        {...other}
        autoFocus
        getInputRef={ref}
        onValueChange={(values) => {
          onChange(values.value);
        }}
        thousandSeparator
        isNumericString
        allowNegative={false}
      />
      <ILS />
    </>
  );
});
interface PeriodInterstRateProps {
  data: ProductsResponse;
  setShowResults: (value: boolean) => void;
  index: number;
}
const PeriodInterstRate: FC<PeriodInterstRateProps> = ({
  index,
  setShowResults,
  data,
}) => {
  const { values } = useFormikContext<Values>();
  return (
    <>
      <Period
        track={values.tracks[index]}
        index={index}
        setShowResults={setShowResults}
        data={data}
      />
      <InterstRate
        name={`tracks[${index}].interestRate`}
        index={index}
        onChange={() => {
          setShowResults(false);
        }}
      />
    </>
  );
};
interface TrackProps {
  track: Track;
  index: number;
  setShowResults: (value: boolean) => void;
  data: ProductsResponse;
  remove: () => void;
  showResults: boolean;
}
export const TrackItem: FC<TrackProps> = ({
  index,
  setShowResults,
  data,
  remove,
  showResults,
}: TrackProps) => {
  const [isEdit, setIsEdit] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [paymentScheduleList, setPaymentScheduleList] =
    useState<PaymentScheduleList[]>();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<Values>();
  const windowWidth = useWindowSize();
  const { EXTRA_SMALL, SMALL, EXTRA_EXTRA_LARGE } = BREAKPOINTS;
  useEffect(() => {
    if (showResults === true) {
      setIsEdit(true);
    }
  }, [showResults]);
  return (
    <>
      {windowWidth <= SMALL && isEdit ? (
        <button
          type="button"
          onClick={() => {
            setIsEdit(false);
            setShowResults(false);
          }}
          className="flex flex-col justify-around p-3 my-4 w-[100%] min-h-[142px] text-[16px] shadow-m p-[21px]"
        >
          <div className="flex flex-row w-full items-center justify-between">
            <div></div>
            <span className="flex-none text-blue border-1 rounded-full h-[18px] w-[18px] text-[9px] leading-[18px]">
              {index + 1}
            </span>
            <Pencil
              className="stroke-blue"
              title={`${t.ACCESSIBILITY.EDIT}}`}
            />
          </div>
          <span className="flex text-grey font-bold py-[12px]">
            {`${formatNumberWithoutDot(values.tracks[index].loanAmount)} ${
              t.ILS_SIGN
            } | ${values.tracks[index].period}  ${t.YEARS} | ${"%"} ${
              values.tracks[index].interestRate
            } ${t.INTEREST}`}
          </span>
          <p className="text-grey text-ellipsis flex-wrap text-right">
            <span className="font-light">{`${t.LOAN_NAME}: `}</span>
            {values.tracks[index].trackName}
          </p>
          <p className="text-grey">
            <span className="font-light"> {`${t.SCHEDULE_NAME}: `}</span>
            {scheduleName}
          </p>
        </button>
      ) : (
        <div className="p-8 my-5 rounded-m shadow-m bg-white" key={index}>
          {index > 0 && (
            <p className="accessibilityHiddenText">{t.REQUIRED_FIELDS_TITLE}</p>
          )}
          <div
            className={`flex items-center ${
              windowWidth < EXTRA_EXTRA_LARGE ? "flex-col" : "flex-row"
            }  `}
          >
            <div
              className={`flex ${
                windowWidth < EXTRA_EXTRA_LARGE ? "pl-0" : "pl-4"
              }`}
            >
              <span className="text-blue border-1 rounded-full h-xl w-xl text-[20px] font-bold text-center leading-[36px]">
                {index + 1}
              </span>
            </div>
            <div
              className={`flex-1 flex justify-between items-center ${
                windowWidth < EXTRA_EXTRA_LARGE ? "flex-col" : "flex-row"
              }   `}
            >
              <div
                className={`${
                  windowWidth > EXTRA_EXTRA_LARGE ? "my-0" : "my-3"
                }`}
              >
                <FormControl
                  variant="standard"
                  className={`${
                    windowWidth < EXTRA_EXTRA_LARGE && windowWidth > EXTRA_SMALL
                      ? "w-[300px]"
                      : "w-[190px]"
                  }`}
                >
                  <TextField
                    inputProps={{'aria-label':t.ACCESSIBILITY.LOAN_AMOUNT}}
                    label={t.LOAN_AMOUNT}
                    value={values.tracks[index].loanAmount}
                    onChange={(value) => {
                      setFieldValue(`tracks[${index}].loanAmount`, value);
                      setShowResults(false);
                      if (10000000 < +value)
                        setFieldTouched(`tracks[${index}].loanAmount`);
                    }}
                    name="numberformat"
                    id="mui-select-standard-label-loan-amount"
                    InputProps={{
                      inputComponent: NumberFormatCustom as any,
                    }}
                    variant="standard"
                  />
                </FormControl>
                <ErrorMessage
                  name={`tracks[${index}].loanAmount`}
                  component="div"
                  className="absolute text-red"
                />
              </div>
              <div
                className={`${
                  windowWidth > EXTRA_EXTRA_LARGE ? "my-0" : "my-3"
                }`}
              >
                <FormControl
                  variant="standard"
                  className={`${
                    windowWidth > EXTRA_EXTRA_LARGE
                      ? "w-[365px]"
                      : windowWidth > EXTRA_SMALL
                      ? "w-[300px]"
                      : "w-[190px]"
                  }`}
                >
                  <InputLabel id="mui-select-standard-label-loan-name">
                    {t.LOAN_NAME}
                  </InputLabel>
                  <Select
                    labelId="mui-select-standard-label-loan-name"
                    id="mui-select-standard-loanName"
                    value={values.tracks[index].trackName}
                    renderValue={(value) => value}
                    onChange={(event) => {
                      setFieldValue(
                        `tracks[${index}].trackName`,
                        event.target.value
                      );
                      const product = data.products.find(
                        (product) => product.trackName == event.target.value
                      );
                      setPaymentScheduleList(product?.paymentScheduleList);
                      setFieldValue(
                        `tracks[${index}].productCode`,
                        product?.productCode
                      );
                      setScheduleName("");
                      setFieldValue(`tracks[${index}].period`, "");
                      setFieldValue(`tracks[${index}].scheduleId`, "");
                      setFieldValue(`tracks[${index}].interestRate`, "");
                      setShowResults(false);
                    }}
                    label="name"
                    IconComponent={Arrow}
                  >
                    {uniq(data.products).map((product) => {
                      return (
                        <MenuItem
                          key={product.productCode}
                          value={product.trackName}
                        >
                          <div className="wrapperLiText">
                            {product.trackName}
                          </div>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <ErrorMessage
                  name={`tracks[${index}].productCode`}
                  component="div"
                  className="absolute text-red"
                />
              </div>
                           <div
                className={`${
                  windowWidth > EXTRA_EXTRA_LARGE ? "my-0" : "my-3"
                }`}
              >
                <FormControl
                  variant="standard"
                  className={`${
                    windowWidth < EXTRA_EXTRA_LARGE && windowWidth > EXTRA_SMALL
                      ? "w-[300px]"
                      : "w-[190px]"
                  }`}
                >
                  {/* select-label-schedule */}
                  <InputLabel id="mui-select-standard-label-schedule">
                    {t.SCHEDULE_NAME}
                  </InputLabel>
                  <Select
                    labelId="mui-select-standard-label-schedule"
                    id="mui-select-schedule-name"
                    value={scheduleName}
                    renderValue={(value) => value}
                    onChange={(event) => {
                      setScheduleName(event.target.value);
                      setFieldValue(
                        `tracks[${index}].scheduleId`,
                        paymentScheduleList?.find(
                          (obj) => event.target.value == obj.scheduleName
                        )?.scheduleId ?? ""
                      );
                      setFieldValue(`tracks[${index}].period`, "");
                      setFieldValue(`tracks[${index}].interestRate`, "");
                      setShowResults(false);
                    }}
                    label="scheduleName"
                    IconComponent={Arrow}
                  >
                    {paymentScheduleList
                      ?.map((payment) => {
                        return {
                          id: payment.scheduleId.toString(),
                          name: payment.scheduleName,
                        };
                      })
                      .map(({ id, name }) => (
                        <MenuItem key={id} value={name}>
                          <div className="wrapperLiText">{name}</div>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <ErrorMessage
                  name={`tracks[${index}].scheduleId`}
                  component="div"
                  className="absolute text-red"
                />
              </div>
              {windowWidth > EXTRA_EXTRA_LARGE ? (
                <PeriodInterstRate
                  data={data}
                  setShowResults={setShowResults}
                  index={index}
                />
              ) : (
                <div
                  className={`flex justify-between ${
                    windowWidth > EXTRA_SMALL ? "w-[300px]" : "w-[190px]"
                  }`}
                >
                  <PeriodInterstRate
                    data={data}
                    setShowResults={setShowResults}
                    index={index}
                  />
                </div>
              )}
            </div>
            {values.tracks.length > 1 && (
              <div
                className={`flex items-center ${
                  windowWidth > EXTRA_EXTRA_LARGE ? "mt-0 pr-4" : "mr-0 mt-5"
                }`}
              >
                <button type="button" onClick={remove}>
                  <CircleTrash
                    title={`${t.ACCESSIBILITY.DELETE_TRACK_NUMBER_BUTTON} ${
                      index + 1
                    }`}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
const initialValues: Values = {
  tracks: [
    {
      loanAmount: "",
      productCode: "",
      period: "",
      trackName: "",
      scheduleId: "",
      interestRate: "",
    },
  ],
};
const validationSchema = Yup.object().shape({
  tracks: Yup.array()
    .of(
      Yup.object().shape({
        loanAmount: Yup.number()
          .min(50000, "טווח הזנה לשדה 50,000-10,000,000")
          .max(10000000, "טווח הזנה לשדה 50,000-10,000,000")
          .required("יש להזין סכום משכנתא")
          .typeError("יש להזין ספרות בלבד"),
        productCode: Yup.number().required("שדה חובה"),
        period: Yup.number().required("שדה חובה"),
        scheduleId: Yup.number().required("שדה חובה"),
        interestRate: Yup.number()
          .typeError("יש להזין ספרות בלבד")
          .required("יש להזין ריבית שנתית")
          .min(0.01, "ריבית שנתית לא חוקית")
          .max(10.0, "ריבית שנתית לא חוקית"),
      })
    )
    .required()
    .max(5),
});


const CalculateByProduct = () => {

  const fieldRefs = useRef<(HTMLInputElement | null)[][]>([]); // שמירה על רפרנסים לשדות
  const [showResults, setShowResults] = useState(false);
  const [resultError, setResultError] = useState(false);
  const [open, setOpen] = useState(true);
  const resRef = useRef<HTMLDivElement>(null);
  const API_AND_URLS = useContext(APIContext);
  const productsUrl =
    API_AND_URLS !== undefined ? API_AND_URLS.API.GET.PRODUCTS : "";
  const { isLoading, error, data } = useQuery<ProductsResponse, Error>(
    "Products",
    () =>
      fetch(productsUrl).then((res) => {
        if (res.status !== 200) {
          throw new Error();
        }
        return res.json();
      }),
    {
      retry: false,
      enabled: onlineManager.isOnline(),
    }
  );
  if (isLoading) {
    return <Loader />;
  }
  if (error || isUndefined(data)) {
    return <ErrorModal open={open} setOpen={setOpen} />;
  }
  const submit = (values: Values) => {
    for (let i = 0; i < values.tracks.length; i++) {
      const track = values.tracks[i];
      // בדיקת השדות הריקים
      if (!track.loanAmount) {
        fieldRefs.current[i][0]?.scrollIntoView({ behavior: "smooth" });
        fieldRefs.current[i][0]?.focus();
        return;
      }
      if (!track.productCode) {
        fieldRefs.current[i][1]?.scrollIntoView({ behavior: "smooth" });
        fieldRefs.current[i][1]?.focus();
        return;
      }
      if (!track.period) {
        fieldRefs.current[i][2]?.scrollIntoView({ behavior: "smooth" });
        fieldRefs.current[i][2]?.focus();
        return;
      }
      if (!track.scheduleId) {
        fieldRefs.current[i][3]?.scrollIntoView({ behavior: "smooth" });
        fieldRefs.current[i][3]?.focus();
        return;
      }
      if (!track.interestRate) {
        fieldRefs.current[i][4]?.scrollIntoView({ behavior: "smooth" });
        fieldRefs.current[i][4]?.focus();
        return;
      }
    }
    let Label = 0;
    
    values.tracks.map((track) => (Label += +track.loanAmount));

    
    window.dataLayer.push({
      Category: "Mortgage Calculator",
      Action: "Calculate",
      event: "auto_event",
      Label: rangeNumber(Label),
      ...getPayloadProducts(values, data),
    });
    setShowResults(true);
    setResultError(false);
    setTimeout(() => {
      resRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    setOpen(true);
   
   

  
  };
  return (
    <div className="mx-auto my-0 xs:w-[100%] xxl:w-[90%]">
      <h2 className="text-darkBlue font-bold xs:text-[24px] xxl:text-[40px] mt-5">
        {t.CALCULATE_BY_PRODUCTS_TITLE}
      </h2>
      <span className="text-grey my-3 inline-block">
        {t.REQUIRED_FIELDS_TITLE}
      </span>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={submit}       
      >
        {({ values }) => (
          <Form className="text-right">
            <FieldArray name="tracks">
              {({ remove, push }) => (
                <div className=" flex-col items-center">
                  {values.tracks.length > 0 &&
                    values.tracks.map((track, index) => (
                      <TrackItem
                        key={index}
                        track={track}
                        index={index}
                        setShowResults={setShowResults}
                        data={data}
                        remove={() => remove(index)}
                        showResults={showResults}
                      />
                    ))}
                  {values.tracks.length < 5 && (
                    <div>
                      <button                     
                        type="button"
                        className="text-blue text-[24px] mt-[25px]"
                        onClick={() => {
                          setShowResults(false);
                          push({
                            loanAmount: "",
                            productCode: "",
                            period: "",
                            scheduleId: "",
                            interestRate: "",
                          });
                        }}
                      >
                        {t.ADD_TRACK}
                      </button>
                      <div>
                        <label className="text-darkBlue text-16">
                          {t.CALCULATE_BY_PRODUCTS_SUB_TITLE}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </FieldArray>
            <div className="xs:my-[25px] xxl:my-[40px]">
              <hr className="text-lightGrey" />
            </div>
            <div className="flex flex-col items-center">
              <div className="text-[24px] font-bold">
                {sum(values.tracks.map((val) => +val.loanAmount)) > 0 && (
                  <label className="text-darkBlue text-[24px]">
                    {`${t.TOTAL} ${t.ILS_SIGN}${formatNumberWithoutDot(
                      sum(
                        values.tracks.map((val) => +val.loanAmount)
                      ).toString()
                    )}`}
                  </label>
                )}
              </div>
              {(resultError || !showResults) && (
                <button
                  type="submit"                 
                  className="mt-[24px] bg-darkBlue text-white xxl:text-[20px] xs:text-[18px] font-bold py-2 px-10 rounded-m text-center" >
                  {t.SHOW_RESULTS_BUTTON}
                </button>
              )}
              <div ref={resRef}>
                {showResults && (
                  <CalculateByProductResults
                    setResultError={setResultError}
                    tracks={values.tracks as unknown as loan[]}
                    paramsData={data}
                    open={open}
                    setOpen={setOpen}
                  />
                )}
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
export default CalculateByProduct;